import { Button } from '@sweep-io/sweep-design';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { MarkdownWrapper, MarkdownWrapperProps } from './MarkdownWrapper';

/**
 * MarkdownWithReply component with text selection reply functionality.
 *
 * Note: We use a custom positioned Box instead of MUI Popover because:
 * - MUI Popover creates a temporary anchor element in document.body
 * - This temporary element interferes with scroll events when text is selected
 * - The anchor element blocks touchpad/mouse wheel scrolling while text is selected
 * - Using a simple positioned Box eliminates scroll interference while maintaining functionality
 */
export const MarkdownWithReply = ({
  markdownWrapperProps,
  onTextSelection,
}: {
  markdownWrapperProps: MarkdownWrapperProps;
  onTextSelection: (text: string | null) => void;
}) => {
  const [selectedText, setSelectedText] = useState('');
  const [popoverPosition, setPopoverPosition] = useState<{ x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const reset = useCallback(() => {
    setSelectedText('');
    setPopoverPosition(null);
  }, []);

  const handleMouseUp = useCallback(() => {

    const selection = window.getSelection();
    if (!selection || selection.toString().trim() === '') {
      reset();
      return;
    }

    // Check if the selection is within this container
    const isSelectionInThisContainer = containerRef.current?.contains(selection.anchorNode);
    if (!isSelectionInThisContainer) {
      return;
    }

    const text = selection.toString().trim();
    setSelectedText(text);

    // Calculate position for the popup relative to the container
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();

    if (containerRect) {
      setPopoverPosition({
        x: rect.x + rect.width / 2 - containerRect.x, // center of the selection
        y: rect.top - 40 - containerRect.y, // on top of the selection (moved up by 40px total)
      });
    }
  }, [reset]);

  // Listen for selection changes to hide button when text is unselected
  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.toString().trim() === '') {
        reset();
      }
    };

    if (selectedText) {
      document.addEventListener('selectionchange', handleSelectionChange);
      return () => document.removeEventListener('selectionchange', handleSelectionChange);
    }
  }, [selectedText, reset]);

  const handleReply = useCallback(() => {
    if (onTextSelection && selectedText) {
      onTextSelection(selectedText);
      reset();
      window.getSelection()?.removeAllRanges();
    }
  }, [onTextSelection, reset, selectedText]);

  const open = Boolean(popoverPosition) && selectedText !== '';

  return (
    <Box ref={containerRef} onMouseUp={handleMouseUp} sx={{ position: 'relative' }}>
      <MarkdownWrapper {...markdownWrapperProps} />
      {open && popoverPosition && (
        <Box
          sx={{
            position: 'absolute',
            left: popoverPosition.x,
            top: popoverPosition.y,
            zIndex: 1000,
            transform: 'translateX(-50%)',
          }}
        >
          <Button variant="system" onClick={handleReply} size="small" startIconName="Quote">
            Reply
          </Button>
        </Box>
      )}
    </Box>
  );
};
